// Mirrors Finkit.Tenants.Admin.Providers contracts.

// Finkit.Enums.Providers.ConditionalFee (numeric values are the wire contract).
export enum ConditionalFee {
  WithConditions = 1,
  WithoutConditions = 2,
}

// Finkit.Enums.Providers.ConditionType.
export enum ConditionType {
  None = 0,
  LessThan = 1,
  GreaterThan = 2,
  Between = 3,
}

// Finkit.Enums.Providers.AccountType.
export enum AccountType {
  ProviderAssets = 0,
  CorporateFunds = 1,
}

// GetListForDataTable row (ProviderDto : EntityDto<Guid>).
export interface ProviderDto {
  id: string;
  name: string;
  searchKeyword: string;
  type: string;
  hasAccount: boolean;
  providerAccountId?: string | null;
}

// ProviderDatatableDto : PaginatedInputDto.
export interface ProviderDatatableInput {
  skipCount: number;
  maxResultCount: number;
  sorting?: string;
  draw?: number;
  search?: string;
  name?: string;
  type?: string;
  searchKeyword?: string;
}

export interface CreateProviderDto {
  name: string;
  type: string;
  searchKeyword: string;
}

export interface UpdateProviderDto {
  name: string;
  type: string;
  searchKeyword: string;
}

export interface ProviderTypeItemDto {
  id: number;
  name: string;
}

export interface CurrencyItemDto {
  id: number;
  name: string;
}

export interface LinkableAccountDto {
  id: string;
  name: string;
}

export interface ProviderAccountLookupDto {
  currencies: CurrencyItemDto[];
  timeZones: string[];
  linkableAccounts: LinkableAccountDto[];
}

export interface ProviderAccountFeeDto {
  depositPercentageFee: number;
  withdrawalPercentageFee: number;
  depositFixedFee: number;
  withdrawalFixedFee: number;
  threshold?: number | null;
  thresholdMaxIfConditionBetween?: number | null;
  conditionType: ConditionType;
  creationTime: string;
}

export interface ProviderAccountFeeInputDto {
  depositPercentageFee: number;
  withdrawalPercentageFee: number;
  depositFixedFee: number;
  withdrawalFixedFee: number;
  threshold?: number | null;
  thresholdMaxIfConditionBetween?: number | null;
}

export interface ProviderAccountDto {
  id: string;
  providerId: string;
  providerName: string;

  currencyId: number;
  currencyName: string;

  startingBalance: number;
  startingBalanceDate: string;
  holdingReserves: number;
  rollingDays: number;

  providerStatementStartingDate?: string | null;
  providerTimeZone: string;
  minMatchPercentage: number;

  ownership: string;
  accountType: AccountType;
  processDayMinus1: boolean;
  excludeFromProviderAssets: boolean;
  includeStatementsInProviderAssets: boolean;

  dateSimilarityPercentage: number;
  amountSimilarityPercentage: number;
  countrySimilarityPercentage: number;
  providerNameSimilarityPercentage: number;

  feesCondition: ConditionalFee;
  transactionPlusXDays: number;
  linkedProviderAccountIds: string[];

  feesWithoutConditions: ProviderAccountFeeDto;
  lessThanFees: ProviderAccountFeeDto;
  betweenFees: ProviderAccountFeeDto;
  greaterThanFees: ProviderAccountFeeDto;
}

export interface ProviderAccountFormDto {
  providerId: string;
  currencyId: number;
  startingBalance: number;
  startingBalanceDate: string | null;
  holdingReserves: number;
  rollingDays: number;

  providerStatementStartingDate?: string | null;
  providerTimeZone: string;
  minMatchPercentage: number;

  ownership: string;
  accountType: AccountType;
  processDayMinus1: boolean;
  excludeFromProviderAssets: boolean;
  includeStatementsInProviderAssets: boolean;

  dateSimilarityPercentage: number;
  amountSimilarityPercentage: number;
  countrySimilarityPercentage: number;
  providerNameSimilarityPercentage: number;

  feesCondition: ConditionalFee;
  transactionPlusXDays: number;
  linkedProviderAccountIds: string[];

  feesWithoutConditions: ProviderAccountFeeInputDto;
  lessThanFees: ProviderAccountFeeInputDto;
  betweenFees: ProviderAccountFeeInputDto;
  greaterThanFees: ProviderAccountFeeInputDto;
}

// ProviderAccountLogRowDto — only the columns rendered in the Update Logs grid.
export interface ProviderAccountLogRowDto {
  userName: string;
  creationTime: string;
  oldHoldingReserves: number;
  newHoldingReserves: number;
  oldRollingDays: number;
  newRollingDays: number;
  oldOwnership: string;
  newOwnership: string;
  oldProcessSwitch: string;
  newProcessSwitch: string;
  oldTransactionPlusXDays: number;
  newTransactionPlusXDays: number;
  oldConnectedClientBankAccount: string;
  newConnectedClientBankAccount: string;
  oldIncludeStatementsInProviderAssets: string;
  newIncludeStatementsInProviderAssets: string;
}

// ProviderAccountFeeRowDto — Update Fees Logs grid.
export interface ProviderAccountFeeRowDto {
  creationTime: string;
  depositFixedFee: number;
  withdrawalFixedFee: number;
  depositPercentageFee: number;
  withdrawalPercentageFee: number;
  threshold?: number | null;
  thresholdMaxIfConditionBetween?: number | null;
  conditionType: string;
}

export interface ProviderAccountLogDatatableInput {
  skipCount: number;
  maxResultCount: number;
  sorting?: string;
  draw?: number;
  search?: string;
  providerAccountId: string;
}

export interface ProviderAccountFeeDatatableInput {
  skipCount: number;
  maxResultCount: number;
  sorting?: string;
  draw?: number;
  search?: string;
  providerAccountId: string;
}
